import React, {
  useState,
  useEffect,
  useCallback,
  type ReactElement,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  checkIsAvailableCountyCode,
  getCountryCodeByIp,
  getCountryName,
  type CountryCode,
} from '@pushwoosh/geo-ip';

import { useAPI } from '~/src/hooks/useAPI';
import { trackEvent } from '~/src/helpers/trackEvent';
import { browserTimezone } from '~/src/helpers/common.helpers';
import { getCookie } from '~/src/helpers/cookies';
import { useEnvironment } from '~/src/contexts/environment';
import { RECAPTCHA_VERSION, isYandexCaptcha } from '~/src/components/Recaptcha';
import { useLang } from '~/src/polyglot';

import { RegisterPage } from './Register.view';
import { SuccessPage } from './components/SuccessPage';

import type { RegisterHandlerValues } from './Register.types';

export function RegisterPageWithSSO(): ReactElement {
  const [countryCode, setCountryCode] = useState<CountryCode | null>(null);
  const [isLoadedCountry, setIsLoadedCountry] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successResult, setSuccessResult] = useState({} as { email: string });
  const [inviteEmail, setInviteEmail] = useState('');
  const [isLoadedInviteEmail, setIsLoadedInviteEmail] = useState(true);
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite') || '';
  const plan = searchParams.get('plan') || '';
  const { callAPI } = useAPI();
  const { getApiOrigin, productName } = useEnvironment();
  const lang = useLang();

  const fetchData = useCallback(() => {
    const payload = new URLSearchParams();
    if (inviteCode == null) {
      return;
    }
    setIsLoadedInviteEmail(false);
    payload.append('code', inviteCode);

    fetch(
      '/api/validate-invite',
      {
        method: 'POST',
        body: payload,
      },
    )
      .then((res) => {
        if (res.status === 200) {
          res.json()
            .then(({ email }) => {
              setInviteEmail(email);
            });
        }
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => setIsLoadedInviteEmail(true));
  }, [inviteCode]);

  useEffect(() => {
    fetchData();
    getCountryCodeByIp()
      .then((code) => {
        if (checkIsAvailableCountyCode(code)) {
          setCountryCode(code);
        }
      })
      .finally(() => {
        setIsLoadedCountry(true);
      });
  }, [fetchData]);

  const registerHandler = useCallback(({
    token,
    email,
    firstName,
    lastName,
    phone,
  }: RegisterHandlerValues) => {
    const referralHash = getCookie('__PW_REFERRAL_PROGRAM_CODE__');

    return fetch(
      `${getApiOrigin('user-api')}/register`,
      {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          country: countryCode ? getCountryName(countryCode) : '',
          countryCode,
          timezone: browserTimezone(),
          email,
          phone,
          recaptchaResponse: token,
          captchaVersion: RECAPTCHA_VERSION,
          ...isYandexCaptcha(productName) && { captchaType: 'yandex' },
          ...referralHash && { referral_hash: referralHash },
          ...inviteCode && { invite: inviteCode },
          ...plan && { plan },
        }),
      },
    )
      .then(async (res) => {
        const { message, error } = await res.json().catch(() => ({}));

        if (res.status === 200) {
          trackEvent({
            name: 'REGISTER',
            properties: {
              email,
              country: countryCode ? getCountryName(countryCode) : '',
            },
          });

          await callAPI('api/register', {});
          setSuccessResult({ email });
        } else if (error === 'invalid recaptcha token') {
          throw new Error(lang({ message: 'REGISTER.ERROR.CAPTCHA_FAILED' }));
        } else {
          // user-api puts the reason into `error`, not `message`
          throw new Error(error || message);
        }
      })
      .catch((error) => setErrorMessage(error.message || lang({ message: 'REGISTER.ERROR.GENERIC' })));
  }, [inviteCode, plan, getApiOrigin, productName, countryCode, callAPI, lang]);

  return (
    successResult.email ? (
      <SuccessPage email={successResult.email} />
    ) : (
      <RegisterPage
        inviteCode={inviteCode}
        plan={plan}
        inviteEmail={inviteEmail}
        isLoadedInviteEmail={isLoadedInviteEmail}
        isLoadedCountry={isLoadedCountry}
        errorMessage={errorMessage}
        registerHandler={registerHandler}
      />
    )
  );
}
